import React, { useState } from "react";
import {
  Box,
  Button,
  Chip,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Stack,
  Switch,
  Typography,
} from "@mui/material";
import { Add, Delete, Edit, ImportExport } from "@mui/icons-material";
import { ChooseColumnDialog } from "./ChooseColumnDialog";
import { ChooseColumnDialogPhases, SelectableCustomColumn } from "./types";
import { SpendSheetConfigDialog } from "./SpendSheetConfigDialog";
import {
  serializeSpendSheetConfig,
  SpendSheetConfig,
} from "./spendSheetConfig";
import { EXPORT_INCLUSION_TYPES } from "@/components/Export/types";
import { useFeatureFlag } from "@/hooks/useLaunchDarkly";

const ENABLE_SPEND_SHEET_CONFIG_FILE_FF_KEY = "enableSpendSheetConfigFile";

interface Props {
  customColumns: SelectableCustomColumn[];
  addCustomColumn: (column: Omit<SelectableCustomColumn, "id">) => string;
  updateCustomColumn: (
    id: string,
    updates: Partial<SelectableCustomColumn>
  ) => void;
  deleteCustomColumn: (id: string) => void;
  toggleColumnSelection: (id: string) => void;
  replaceColumns: (
    columns: (Omit<SelectableCustomColumn, "id" | "phase"> & {
      phase?: ChooseColumnDialogPhases;
    })[]
  ) => void;
  spendInclusion: EXPORT_INCLUSION_TYPES;
  setSpendInclusion: (inclusion: EXPORT_INCLUSION_TYPES) => void;
  isCloudMaturityEnabled: boolean;
  setIsCloudMaturityEnabled: (enabled: boolean) => void;
}

export const ProductsAndVendorsOptions: React.FC<Props> = (props) => {
  const {
    customColumns,
    addCustomColumn,
    updateCustomColumn,
    deleteCustomColumn,
    toggleColumnSelection,
    replaceColumns,
    spendInclusion,
    setSpendInclusion,
    isCloudMaturityEnabled,
    setIsCloudMaturityEnabled,
  } = props;

  const isConfigFileEnabled = useFeatureFlag(
    ENABLE_SPEND_SHEET_CONFIG_FILE_FF_KEY
  );

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingColumn, setEditingColumn] =
    useState<SelectableCustomColumn | null>(null);
  const [isConfigDialogOpen, setIsConfigDialogOpen] = useState(false);

  const currentConfigJson = serializeSpendSheetConfig({
    spendInclusion,
    cloudMaturityEnabled: isCloudMaturityEnabled,
    customColumns,
  });

  const handleLoadConfig = (config: SpendSheetConfig) => {
    setSpendInclusion(config.spendInclusion);
    setIsCloudMaturityEnabled(config.cloudMaturityEnabled);
    replaceColumns(config.customColumns);
  };

  const handleAddColumn = () => {
    setEditingColumn(null);
    setIsDialogOpen(true);
  };

  const handleEditColumn = (column: SelectableCustomColumn) => {
    setEditingColumn(column);
    setIsDialogOpen(true);
  };

  const handleDeleteColumn = (id: string) => {
    deleteCustomColumn(id);
  };

  const handleToggleColumn = (id: string) => {
    toggleColumnSelection(id);
  };

  const handleDialogClose = () => {
    setIsDialogOpen(false);
    setEditingColumn(null);
  };

  const handleDialogSave = (columnData: Omit<SelectableCustomColumn, "id">) => {
    if (editingColumn) {
      updateCustomColumn(editingColumn.id, columnData);
    } else {
      addCustomColumn(columnData);
    }
    handleDialogClose();
  };

  const getDataTypeLabel = (dataType: string) => {
    switch (dataType) {
      case "Y_N":
        return "Yes/No";
      case "ONE_ZERO":
        return "1/0";
      case "PRODUCT_COUNT":
        return "Product Count";
      default:
        return dataType;
    }
  };

  return (
    <Box sx={{ p: 3, width: "70%" }}>
      <Stack spacing={3}>
        <Typography variant="h6" component="h2">
          Products & Vendors Custom Columns
        </Typography>

        <Typography variant="body2" color="text.secondary">
          Create custom columns to show product or vendor presence data for each
          company in your export.
        </Typography>

        <Stack direction="row" spacing={2} sx={{ alignSelf: "flex-start" }}>
          <Button
            data-testid="spend-sheet-add-column-btn"
            variant="outlined"
            startIcon={<Add />}
            onClick={handleAddColumn}
          >
            Add Custom Column
          </Button>
          {isConfigFileEnabled && (
            <Button
              data-testid="spend-sheet-config-btn"
              variant="outlined"
              color="secondary"
              startIcon={<ImportExport />}
              onClick={() => setIsConfigDialogOpen(true)}
            >
              Download / Load Config
            </Button>
          )}
        </Stack>

        {customColumns.length > 0 && (
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 2 }}>
              Custom Columns ({customColumns.length})
            </Typography>
            <List>
              {customColumns.map((column) => (
                <ListItem
                  key={column.id}
                  sx={{
                    border: 1,
                    borderColor: "divider",
                    borderRadius: 1,
                    mb: 1,
                  }}
                >
                  <ListItemText
                    primary={
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Typography variant="subtitle2">
                          {column.name}
                        </Typography>
                        <Chip
                          label={
                            column.type === "product" ? "Products" : "Vendors"
                          }
                          size="small"
                          color={
                            column.type === "product" ? "primary" : "secondary"
                          }
                        />
                        <Chip
                          label={getDataTypeLabel(column.dataType)}
                          size="small"
                          variant="outlined"
                        />
                      </Stack>
                    }
                    secondary={`${column.ids.length} ${column.type}${column.ids.length !== 1 ? "s" : ""} selected`}
                  />
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Switch
                      checked={column.isSelected}
                      onChange={() => handleToggleColumn(column.id)}
                      size="small"
                    />
                    <IconButton
                      size="small"
                      onClick={() => handleEditColumn(column)}
                    >
                      <Edit fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleDeleteColumn(column.id)}
                      color="error"
                    >
                      <Delete fontSize="small" />
                    </IconButton>
                  </Stack>
                </ListItem>
              ))}
            </List>
          </Box>
        )}

        {customColumns.length === 0 && (
          <Box
            sx={{
              textAlign: "center",
              py: 4,
              color: "text.secondary",
              border: 1,
              borderColor: "divider",
              borderRadius: 1,
              borderStyle: "dashed",
            }}
          >
            <Typography variant="body2">
              No custom columns created yet. Click "Add Custom Column" to get
              started.
            </Typography>
          </Box>
        )}
      </Stack>

      <ChooseColumnDialog
        open={isDialogOpen}
        onClose={handleDialogClose}
        onSave={handleDialogSave}
        editingColumn={editingColumn}
      />

      <SpendSheetConfigDialog
        open={isConfigDialogOpen}
        onClose={() => setIsConfigDialogOpen(false)}
        currentConfigJson={currentConfigJson}
        onLoad={handleLoadConfig}
      />
    </Box>
  );
};
